import React from 'react';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { displayDate, DATE_FORMAT_FULL_WITH_TIME } from 'utils/date';
import { Popover } from 'ui/popover';
import {
  parseToDate,
  presets,
  parseTimeRangeToLabelDef,
  UNIT_MODIFIER_VALUES,
} from 'utils/relativeDateTime';
import noop from 'utils/noop';
import PresetsPanel from './panels/PresetsPanel';
import CustomPanel from './panels/CustomPanel';
import {
  RelativeLabelContainer,
  DateLabelContainer,
  DateLabel,
  StyledSemanticAccordion,
  ButtonLabel,
  StyledSemanticIcon,
  StyledPopoverContent,
  StyledSemanticButton,
  StyledPopoverTrigger,
  MainContainer,
} from './DateRangePickerRelative.styled';

// type Props = {
//   /** Additional class name. */
//   className: string,
//   /** Combines start and end range to single relative date. */
//   combineToAndFrom: boolean,
//   /** DateRangePicker is disabled. */
//   disabled?: boolean,
//   /** End range of the relative time picker. */
//   endRange: string,
//   /** When selecting a relative date in popup, it hides the calculated date range label from input. */
//   hideParsedDateLabelFromInput: boolean,
//   /**
//    * Callback function when start or end range changes.
//    *
//    * ```
//    * onChangeRange({ startRange, endRange }}) => {}
//    * ```
//    * @param { Object } updatedRanges: Object containing updated date ranges.
//    * @returns { void }
//    */
//   onChangeRange: (endRange: string) => void,
//   /**
//    * Callback function when end range changes.
//    *
//    * ```
//    * onEndRangeChange(endRange) => {}
//    * ```
//    * @param { String } endRange: The updated end range.
//    * @returns { void }
//    */
//   onEndRangeChange: (endRange: string) => void,
//   /**
//    * Callback function when start range changes.
//    *
//    * ```
//    * onStartRangeChange(startRange) => {}
//    * ```
//    * @param { String } startRange: The updated start range.
//    * @returns { void }
//    */
//   onStartRangeChange: (startRange: string) => void,
//   /** Panels to show in relative date picker. */
//   panels: [React.Component],
//   /** Show from fields. */
//   showFrom: boolean,
//   /** Display in popup. */
//   showInPopup: boolean,
//   /** Show to fields. */
//   showTo: boolean,
//   /** Start range of the relative time picker. */
//   startRange: string,
//   /** Unparse relative date value */
//   value: string,
// };

export default class DateRangePickerRelative extends React.Component {
  static defaultProps = {
    combineToAndFrom: false,
    hideParsedDateLabel: false,
    onChangeRange: noop,
    onEndRangeChange: noop,
    onStartRangeChange: noop,
    panels: [PresetsPanel, CustomPanel],
    showInPopup: false,
    disabled: false,
    ...presets.LAST_5_DAYS,
  };

  constructor(props) {
    super(props);
    const { isPreset } = parseTimeRangeToLabelDef(props.startRange, props.endRange);
    this.state = {
      activeIndex: isPreset ? 0 : 1,
      startDateLabel: '',
      endDateLabel: '',
    };
  }

  /**
   * componentDidMount
   * Update current time range label on mount.
   * @return { void }
   */
  componentDidMount = () => {
    this.displayCurrentTime();
  };

  /**
   * displayCurrentTime
   * Displays the current date range based on selected relative time range.
   * @param { String } updatedStart: New startRange value.
   * @param { String } updatedEnd: New endRange value.
   * @return { void }
   */
  displayCurrentTime = (updatedStart, updatedEnd) => {
    const { startRange, endRange } = this.props;
    const startDateLabel = displayDate(
      parseToDate(updatedStart || startRange),
      DATE_FORMAT_FULL_WITH_TIME,
    );
    const endDateLabel = displayDate(
      parseToDate(updatedEnd || endRange),
      DATE_FORMAT_FULL_WITH_TIME,
    );
    return { startDateLabel, endDateLabel };
  };

  /**
   * handleTitleClick
   * Callback function when title is clicked. Sets activeIndex to the index of the clicked title.
   * @param { Object } e: The click event.
   * @param { Object } titleProps: The props of the title containing the index.
   * @return { void }
   */
  handleTitleClick = (e, { index }) => {
    this.setState({ activeIndex: index });
  };

  /**
   * handleRangeChange
   * Callback function the relative range(s) change
   * @param { Object } updatedValues: Object containing updated startRange, endRange.
   * @return { void }
   */
  handleRangeChange = ({ startRange, endRange, isTextInput }) => {
    const { onChangeRange, onStartRangeChange, onEndRangeChange } = this.props;
    if (startRange) {
      onStartRangeChange(startRange);
    }
    if (endRange) {
      onEndRangeChange(endRange);
    }
    onChangeRange({ startRange, endRange, isTextInput });
  };

  render() {
    const { activeIndex } = this.state;
    const {
      className,
      combineToAndFrom,
      disabled,
      endRange,
      hideParsedDateLabelFromInput,
      panels,
      showFrom,
      showInPopup,
      showTo,
      startRange,
      value,
    } = this.props;

    const { startDateLabel, endDateLabel } = this.displayCurrentTime(startRange, endRange);
    const { label, isPreset } = parseTimeRangeToLabelDef(
      typeof startRange === 'string' ? startRange.replace('now', '') : startRange,
      endRange,
    );
    const relativeLabelText = !isPreset ? value : label;

    const panelList = panels.map((Panel, index) => (
      <Panel
        active={activeIndex === index}
        combineToAndFrom={combineToAndFrom}
        endRange={endRange}
        index={index}
        key={`panel-${index}`}
        onTitleClick={this.handleTitleClick}
        onRangeChange={this.handleRangeChange}
        onPresetSelected={this.handlePresetSelected}
        showFrom={showFrom}
        showTo={showTo}
        startRange={startRange}
        value={value}
      />
    ));

    const showAccordionHeader = !showInPopup || hideParsedDateLabelFromInput;
    const relativeLabel = <RelativeLabelContainer>{relativeLabelText}</RelativeLabelContainer>;
    const isToDate = startRange === UNIT_MODIFIER_VALUES.now;
    const showStartLabel = !combineToAndFrom || (combineToAndFrom && !isToDate);
    const showEndLabel = !combineToAndFrom || (combineToAndFrom && isToDate);
    const parsedDateLabel = (
      <DateLabelContainer>
        {showStartLabel && (
          <DateLabel>
            {startDateLabel}
            {showStartLabel && showEndLabel && <span>&nbsp;-&nbsp;</span>}
          </DateLabel>
        )}
        {showEndLabel && <DateLabel>{endDateLabel}</DateLabel>}
      </DateLabelContainer>
    );

    const accordion = (
      <StyledSemanticAccordion styled data-role="accordion">
        {showAccordionHeader && (
          <SemanticUI.Accordion.Title active={true} className="cssDateRangePickerAccordionTitle">
            {!showInPopup && relativeLabel}
            {parsedDateLabel}
          </SemanticUI.Accordion.Title>
        )}
        {panelList}
      </StyledSemanticAccordion>
    );

    if (showInPopup) {
      return (
        <Popover>
          <StyledPopoverContent>{accordion}</StyledPopoverContent>
          <StyledPopoverTrigger asChild>
            <div>
              <StyledSemanticButton disabled={disabled} appearance="semantic" className={className}>
                <ButtonLabel>
                  {relativeLabel}
                  {!hideParsedDateLabelFromInput && parsedDateLabel}
                  <StyledSemanticIcon name="triangle down" />
                </ButtonLabel>
              </StyledSemanticButton>
            </div>
          </StyledPopoverTrigger>
        </Popover>
      );
    }

    return <MainContainer className={className}>{accordion}</MainContainer>;
  }
}
